import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

export interface DashboardKpis {
  totalQuotes: number;
  draftCount: number;
  sentCount: number;
  bookedCount: number;
  declinedCount: number;
  cancelledCount: number;
  totalEstimatedValue: number;
  avgEstimatedValue: number;
  conversionRate: number;
  // Sales tab KPIs
  activeLeads: number;
  quotesSent: number;
  revenueWon: number;
  winRate: number;
}

export interface MonthlyData {
  month: string;
  count: number;
  value: number;
}

export interface RevenueMonthlyData {
  month: string;
  revenue: number;
}

export interface StatusItem {
  status: string;
  count: number;
  label: string;
}

export interface EstimatorData {
  uid: string;
  name: string;
  count: number;
  value: number;
}

export interface FunnelStage {
  stage: string;
  count: number;
}

export interface RecentQuote {
  id: string;
  customerCompanyName: string;
  quoteStatus: 'draft' | 'sent' | 'booked' | 'declined' | 'cancelled';
  estimatedValue: number;
  createdAt: string;
  createdByName: string;
}

export interface RecentActivityItem {
  id: string;
  customerCompanyName: string;
  event: string;
  eventType: 'lead' | 'quote' | 'won' | 'lost';
  createdAt: string;
  createdByName: string;
}

export interface LeadSourceItem {
  source: string;
  count: number;
}

export interface AvailableUser {
  uid: string;
  name: string;
}

export interface ReasonItem {
  reason: string;
  count: number;
}

export interface DashboardData {
  kpis: DashboardKpis;
  quotesOverTime: MonthlyData[];
  revenueOverTime: RevenueMonthlyData[];
  statusDistribution: StatusItem[];
  wonReasonDistribution: ReasonItem[];
  lostReasonDistribution: ReasonItem[];
  quotesByEstimator: EstimatorData[];
  conversionFunnel: FunnelStage[];
  salesFunnel: FunnelStage[];
  leadSourceDistribution: LeadSourceItem[];
  recentQuotes: RecentQuote[];
  recentActivity: RecentActivityItem[];
  availableUsers: AvailableUser[];
}

export type DashboardPeriod = '7d' | '30d' | '90d' | '12m' | 'all';

interface DashboardResponse {
  success: boolean;
  data: DashboardData;
}

export function useDashboard(orgId: string | null, period: DashboardPeriod, userId?: string) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('period', period);
      if (userId) params.set('userId', userId);
      const res = await api.get<DashboardResponse>(`/dashboard/${orgId}?${params}`);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [orgId, period, userId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, refetch: fetchDashboard };
}
